<template>
  <container :isModal="false" :loading="stateLoading">
    <template v-slot:header>
      <h4>Features</h4>
    </template>
    <template v-slot:body>
      <template v-if="stateReady">
        <fieldset :disabled="stateBlocked">
          <DataTable
            :value="features"
            dataKey="idFeature"
            responsiveLayout="stack"
          >
            <Column field="nome" header="Nome" />
            <Column field="descricao" header="Descrição" />
            <Column header="Origem">
              <template #body="slotProps">
                <span v-if="!slotProps.data.flAtivo"
                  >Desativada globalmente</span
                >
                <span v-else-if="!slotProps.data.temRegistro">
                  Herdado (padrão:
                  {{ slotProps.data.flPadrao ? "Sim" : "Não" }})
                </span>
                <span v-else>Definido</span>
              </template>
            </Column>
            <Column header="Habilitada">
              <template #body="slotProps">
                <input-with-label :floatLabel="false" v-slot="l">
                  <InputSwitch
                    v-model="slotProps.data.flHabilitada"
                    :id="l.id"
                    :disabled="!slotProps.data.flAtivo"
                  />
                </input-with-label>
              </template>
            </Column>
          </DataTable>
          <div class="p-d-flex p-jc-end p-mt-2">
            <Button
              type="button"
              class="p-button-rounded"
              label="Salvar Features"
              @click="salvarFeatures"
            />
          </div>
        </fieldset>
      </template>
    </template>
  </container>
</template>

<script lang="ts">
import {
  defineComponent,
  reactive,
  toRefs,
  onMounted,
  computed,
  Ref
} from "vue";
import { useAuth } from "../../../modules/auth";
import { useToast } from "../../../modules/message";
import {
  FeaturePayload,
  FeatureClienteUpdatePayload,
  useFeatureService
} from "../../../modules/business/feature/featureBusiness";

interface FeatureSnapshot {
  idFeature: number;
  flHabilitada: boolean;
}

interface ClienteFeaturesState {
  features: Array<FeaturePayload>;
  original: Array<FeatureSnapshot>;
  stateReady: boolean;
  loadings: Array<Ref<boolean>>;
}

export default defineComponent({
  props: {
    idCliente: { type: Number, required: true },
    parentLoading: Boolean
  },
  setup(props) {
    const state = reactive<ClienteFeaturesState>({
      features: [],
      original: [],
      stateReady: false,
      loadings: []
    });

    const stateLoading = computed(() => {
      return state.loadings.length !== 0 || !!props.parentLoading;
    });

    const stateBlocked = computed(() => {
      return !!(
        stateLoading.value || !useAuth().amIAllowed("edit", "root-cliente")
      );
    });

    const setFeatures = (features: Array<FeaturePayload>) => {
      state.features = features.map((f) => ({
        ...f,
        flHabilitada: f.temRegistro ? f.flHabilitada : f.habilitada
      }));
      state.original = state.features.map((f) => ({
        idFeature: f.idFeature,
        flHabilitada: !!f.flHabilitada
      }));
    };

    onMounted(() => {
      const { data, loading, getFeaturesClienteRoot } = useFeatureService();
      state.loadings.push(loading);
      getFeaturesClienteRoot(props.idCliente)
        .then(() => {
          setFeatures(data.value ?? []);
          state.loadings.splice(state.loadings.indexOf(loading), 1);
          state.stateReady = true;
        })
        .catch(() => {
          state.loadings.splice(state.loadings.indexOf(loading), 1);
        });
    });

    const salvarFeatures = () => {
      const alteradas: Array<FeatureClienteUpdatePayload> = state.features
        .filter((f) => {
          const original = state.original.find(
            (o) => o.idFeature === f.idFeature
          );
          return !original || original.flHabilitada !== !!f.flHabilitada;
        })
        .map((f) => ({
          idFeature: f.idFeature,
          flHabilitada: !!f.flHabilitada
        }));

      if (!alteradas.length) {
        return;
      }

      const { data, loading, updateFeaturesClienteRoot } = useFeatureService();
      state.loadings.push(loading);
      updateFeaturesClienteRoot(props.idCliente, alteradas)
        .then(() => {
          setFeatures(data.value ?? []);
          state.loadings.splice(state.loadings.indexOf(loading), 1);
          useToast().success("Features salvas com sucesso");
        })
        .catch(() => {
          state.loadings.splice(state.loadings.indexOf(loading), 1);
        });
    };

    return {
      stateLoading,
      stateBlocked,
      salvarFeatures,
      ...toRefs(state)
    };
  }
});
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss"></style>
